import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { validateGSTNumber } from '../services/gstCalculations';
import { 
  UserIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function Profile() {
  const { userProfile, updateUserProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: userProfile?.displayName || '',
    businessName: userProfile?.businessName || '',
    gstNumber: userProfile?.gstNumber || '',
    email: userProfile?.email || '',
    phone: userProfile?.phone || '',
    address: userProfile?.address || '',
    city: userProfile?.city || '',
    state: userProfile?.state || '',
    pincode: userProfile?.pincode || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.displayName.trim()) {
      return toast.error('Display name is required');
    }

    if (formData.gstNumber && !validateGSTNumber(formData.gstNumber)) {
      return toast.error('Please enter a valid GST number');
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      return toast.error('Please enter a valid 10-digit phone number');
    }

    setSaving(true);

    try {
      const updateData = {
        ...formData,
        displayName: formData.displayName.trim(),
        businessName: formData.businessName.trim(),
        gstNumber: formData.gstNumber.toUpperCase().trim(),
        phone: formData.phone.replace(/\D/g, ''), // Remove non-digits
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        pincode: formData.pincode.trim()
      };

      await updateUserProfile(updateData);
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      displayName: userProfile?.displayName || '',
      businessName: userProfile?.businessName || '',
      gstNumber: userProfile?.gstNumber || '',
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      address: userProfile?.address || '',
      city: userProfile?.city || '',
      state: userProfile?.state || '',
      pincode: userProfile?.pincode || ''
    });
    setEditing(false);
  };

  const InfoField = ({ label, value, icon: Icon, editable = true, name, type = 'text', placeholder }) => (
    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
      <dt className="text-sm font-medium text-gray-500 flex items-center">
        <Icon className="h-4 w-4 mr-2" />
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
        {editing && editable ? (
          <input
            type={type}
            name={name}
            value={formData[name] || ''}
            onChange={handleInputChange}
            className="input"
            placeholder={placeholder}
          />
        ) : (
          <span className={value ? '' : 'text-gray-400 italic'}>
            {value || 'Not provided'}
          </span>
        )}
      </dd>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Profile Settings
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your account and business information
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="btn-primary"
            >
              Edit Profile
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? (
                  <div className="spinner mr-2"></div>
                ) : (
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Personal Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Your personal details and contact information.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <InfoField
              label="Full Name"
              value={userProfile?.displayName}
              icon={UserIcon}
              name="displayName"
              placeholder="Enter your full name"
            />
            <InfoField
              label="Email Address"
              value={userProfile?.email}
              icon={EnvelopeIcon}
              editable={false}
              name="email"
            />
            <InfoField
              label="Phone Number"
              value={userProfile?.phone}
              icon={PhoneIcon}
              name="phone"
              type="tel"
              placeholder="10-digit phone number"
            />
          </dl>
        </div>
      </div>

      {/* Business Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Business Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Details about your business for GST management.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <InfoField
              label="Business Name"
              value={userProfile?.businessName}
              icon={BuildingOfficeIcon}
              name="businessName"
              placeholder="Enter your business name"
            />
            <InfoField
              label="GST Number"
              value={userProfile?.gstNumber}
              icon={IdentificationIcon}
              name="gstNumber"
              placeholder="e.g., 27XXXXX1234X1ZX"
            />
          </dl>
        </div>
      </div>

      {/* Address Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Address Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Your business address details.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <MapPinIcon className="h-4 w-4 mr-2" />
                Address
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {editing ? (
                  <textarea
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    className="input"
                    rows="3"
                    placeholder="Enter your complete address"
                  />
                ) : (
                  <span className={userProfile?.address ? '' : 'text-gray-400 italic'}>
                    {userProfile?.address || 'Not provided'}
                  </span>
                )}
              </dd>
            </div>
            
            <InfoField
              label="City"
              value={userProfile?.city}
              icon={MapPinIcon}
              name="city"
              placeholder="Enter city"
            />
            
            <InfoField
              label="State"
              value={userProfile?.state}
              icon={MapPinIcon}
              name="state"
              placeholder="Enter state"
            />
            
            <InfoField
              label="PIN Code"
              value={userProfile?.pincode}
              icon={MapPinIcon}
              name="pincode"
              placeholder="6-digit PIN code"
            />
          </dl>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Account Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Account creation and last update details.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Account Created</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {userProfile?.createdAt 
                  ? new Date(userProfile.createdAt.toDate ? userProfile.createdAt.toDate() : userProfile.createdAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'Not available'
                }
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {userProfile?.updatedAt 
                  ? new Date(userProfile.updatedAt.toDate ? userProfile.updatedAt.toDate() : userProfile.updatedAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Not available'
                }
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* GST Status */}
      {userProfile?.gstNumber && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <h4 className="text-sm font-medium text-green-800">GST Registered Business</h4>
              <p className="text-sm text-green-600">
                Your business is registered for GST with number: {userProfile.gstNumber}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Profile Tips</h4>
        <ul className="text-sm text-blue-600 space-y-1">
          <li>• Complete your business information for accurate GST reporting</li>
          <li>• Ensure your GST number is correct for compliance</li>
          <li>• Keep your contact information updated for important notifications</li>
          <li>• Your email address cannot be changed for security reasons</li>
        </ul>
      </div>
    </div>
  );
}

export default Profile;
